import { useEffect, useRef, useState } from "react";
import { Headphones, Loader2, Pause, Play } from "lucide-react";

const fmt = (s) => {
  if (!isFinite(s)) return "0:00";
  const m = Math.floor(s / 60);
  return `${m}:${String(Math.floor(s % 60)).padStart(2, "0")}`;
};

// Plays the pre-generated narration at /audio/{id}.mp3 — fully static.
export default function AudioGuide({ id }) {
  const [state, setState] = useState("idle"); // idle | loading | ready | error
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const audioRef = useRef(null);

  useEffect(() => {
    return () => {
      audioRef.current?.pause();
      audioRef.current = null;
    };
  }, []);

  const handleMain = async () => {
    if (state === "loading") return;
    if (state === "ready") {
      const a = audioRef.current;
      if (!a) return;
      if (playing) {
        a.pause();
        setPlaying(false);
      } else {
        a.play();
        setPlaying(true);
      }
      return;
    }
    setState("loading");
    try {
      const audio = new Audio(`/audio/${id}.mp3`);
      audioRef.current = audio;
      audio.addEventListener("loadedmetadata", () => setDuration(audio.duration));
      audio.addEventListener("timeupdate", () => setProgress(audio.currentTime));
      audio.addEventListener("ended", () => setPlaying(false));
      await audio.play();
      setState("ready");
      setPlaying(true);
    } catch (_) {
      setState("error");
    }
  };

  const seek = (e) => {
    const a = audioRef.current;
    if (!a || !duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    a.currentTime = ((e.clientX - rect.left) / rect.width) * duration;
  };

  return (
    <div
      className="border border-[var(--c-border)] bg-[var(--c-bg-elev)] p-4 sm:p-5 flex items-center gap-4"
      data-testid="audio-guide"
    >
      <button
        onClick={handleMain}
        className="w-12 h-12 shrink-0 rounded-full border border-[var(--c-accent)] text-[var(--c-accent)] hover:bg-[var(--c-accent)] hover:text-[var(--c-bg)] flex items-center justify-center transition-colors"
        aria-label={playing ? "Pause audio guide" : "Play audio guide"}
        data-testid="audio-guide-play-btn"
      >
        {state === "loading" ? (
          <Loader2 size={18} className="animate-spin" />
        ) : playing ? (
          <Pause size={18} />
        ) : (
          <Play size={18} className="ml-0.5" />
        )}
      </button>
      <div className="min-w-0 flex-1">
        <p className="overline flex items-center gap-2">
          <Headphones size={12} /> Audio Guide
        </p>
        {state === "idle" && (
          <p className="text-sm text-[var(--c-text-muted)] mt-1">
            Listen to this chapter — narrated like a museum guide.
          </p>
        )}
        {state === "loading" && (
          <p className="text-sm text-[var(--c-text-muted)] mt-1 italic">
            Loading the narration…
          </p>
        )}
        {state === "error" && (
          <p className="text-sm text-[var(--c-text-muted)] mt-1">
            Couldn't load the narration. Tap to try again.
          </p>
        )}
        {state === "ready" && (
          <div className="mt-2 flex items-center gap-3">
            <div
              className="relative flex-1 h-1.5 bg-[var(--c-border)] cursor-pointer"
              onClick={seek}
              data-testid="audio-guide-progress"
            >
              <div
                className="absolute inset-y-0 left-0 bg-[var(--c-accent)]"
                style={{ width: `${duration ? (progress / duration) * 100 : 0}%` }}
              />
            </div>
            <span className="font-mono-plex text-[10px] text-[var(--c-text-muted)] shrink-0">
              {fmt(progress)} / {fmt(duration)}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
